import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { UNIT_TYPES } from '@ee/common/enums';
import { STATES } from '@ee/common/constants';
import { CustomFieldModule } from '@ee/common/custom-field';
import { CustomField, LayoutItem } from '@ee/common/models';
import { allowMessageEmphasisStyles } from '@ee/common/pipes';
import { PlaintiffOptions } from '@ee/common/utils';
import { AddressBlockComponent } from './address-block.component';
import { PLAINTIFF_FIELD_KEY, PlaintiffNameFieldComponent } from './plaintiff-name-field.component';
import { COMPOSITE_LABELS, COMPOSITE_PARTS, CompositePart } from './composites';
import { customFieldFor } from './field-view';
import { FieldDefinitionView, sameHeading } from './intake-layout.model';
import { SubjectGroupComponent } from './subject-group.component';

/**
 * One placement, drawn: a content block, a composite, a party that repeats, or a field.
 *
 * Every field goes through `ee-field`. That is the whole point of this unit — the renderer, the wizard
 * and the builder canvas draw the same control a firm sees everywhere else, so there is no second
 * per-type switch to fall behind when a type is added.
 */
@Component({
  selector: 'ee-layout-cell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    CustomFieldModule,
    AddressBlockComponent,
    SubjectGroupComponent,
    PlaintiffNameFieldComponent
  ],
  template: `
    @switch (item.kind) {
      @case ('HEADING') {
        <h4 class="rendered__heading">{{ item.text }}</h4>
      }
      @case ('RICH_TEXT') {
        <!-- The message allow-list, not Angular's sanitizer: that one drops the colour the editor offers. -->
        <div class="rendered__rich" [innerHTML]="richHtml"></div>
      }
      @case ('DIVIDER') {
        <hr class="rendered__divider" />
      }
      @case ('COMPOSITE') {
        @if (liveComposites && !builder && item.composite_type === 'ADDRESS' && form) {
          <ee-address-block [form]="form" [parts]="parts" [unitTypes]="unitTypes"></ee-address-block>
        } @else {
          <fieldset class="rendered__composite">
            <legend class="rendered__composite-legend">{{ compositeLabel }}</legend>
            <div class="rendered__composite-parts">
              @for (part of parts; track part.key) {
                @if (part.derived) {
                  <div class="rendered__derived" [attr.data-part]="part.key">
                    <span class="rendered__derived-label">{{ part.label }}</span>
                    <span class="rendered__derived-value">{{ partValue(part.key) || '—' }}</span>
                    <span class="rendered__chip">derived</span>
                  </div>
                } @else if (part.key === unitTypeKey) {
                  <mat-form-field appearance="outline" class="compact full-width" [attr.data-part]="part.key">
                    <mat-label>{{ part.label }}</mat-label>
                    <mat-select [formControl]="partControl(part.key)">
                      @for (option of unitTypes; track option) {
                        <mat-option [value]="option">{{ option }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                } @else if (part.key === stateKey) {
                  <mat-form-field appearance="outline" class="compact full-width" [attr.data-part]="part.key">
                    <mat-label>{{ part.label }}</mat-label>
                    <mat-select [formControl]="partControl(part.key)">
                      @for (state of states; track state) {
                        <mat-option [value]="state">{{ state }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                } @else {
                  <mat-form-field appearance="outline" class="compact full-width" [attr.data-part]="part.key">
                    <mat-label>{{ part.label }}</mat-label>
                    <input matInput [formControl]="partControl(part.key)" />
                  </mat-form-field>
                }
              }
            </div>
          </fieldset>
        }
      }
      @default {
        @if (definition?.subject_fields?.length) {
          <ee-subject-group
            [definition]="definition!"
            [array]="asArray(control)"
            [builder]="builder"
            [legendHidden]="repeatsHeading(definition!.label)"></ee-subject-group>
        } @else if (accountPlaintiffOptions && plaintiffControl; as nameControl) {
          <!--
            EE-367: the client account's plaintiff options. A restricted client may only pick one;
            otherwise free text stays allowed. Property-owner options (PM handoff) do not come here.
          -->
          <ee-plaintiff-name-field [control]="nameControl" [savedNames]="accountPlaintiffOptions.options"
                                   [restricted]="accountPlaintiffOptions.restricted"
                                   [label]="field.label" [required]="!!field.required" [compact]="true"
                                   [hint]="plaintiffHint"></ee-plaintiff-name-field>
        } @else {
          <ee-field [field]="field" [control]="$any(control ?? inert)" [compact]="true"></ee-field>
        }
      }
    }
  `,
  styleUrl: './layout-cell.component.scss'
})
export class LayoutCellComponent {
  @Input({ required: true }) item!: LayoutItem;

  @Input() definition: FieldDefinitionView | null = null;

  /** The control behind this placement: a FormControl, or a FormArray for a party that repeats. */
  @Input() control: AbstractControl | null = null;

  /** The whole form — composites read their parts from it. */
  @Input() form: FormGroup | null = null;

  /** Drawn on the builder canvas: inert, and never runs live lookups. */
  @Input() builder = false;

  /** Whether the address block may geocode and look up counties. Never on the canvas. */
  @Input() liveComposites = false;

  @Input() unitTypes: string[] = UNIT_TYPES;

  /** The heading drawn above this placement's section, so a label that only repeats it can stay quiet. */
  @Input() heading: string | null = null;

  /**
   * EE-367: the client account's plaintiff options, applied to the `plaintiff` field. Null keeps the
   * ordinary control. Ignored on the builder canvas.
   */
  @Input() plaintiffOptions: PlaintiffOptions | null = null;

  protected repeatsHeading(label: string | null | undefined): boolean {
    return sameHeading(label, this.heading);
  }

  /** The placement's control when it is a single field, which is what the plaintiff dropdown binds. */
  protected get plaintiffControl(): FormControl | null {
    return this.control instanceof FormControl ? this.control : null;
  }

  /** Plaintiff options for this placement. The builder canvas never turns a field into a dropdown. */
  protected get accountPlaintiffOptions(): PlaintiffOptions | null {
    if (this.builder || this.item?.field_key !== PLAINTIFF_FIELD_KEY) {
      return null;
    }
    return this.plaintiffOptions;
  }

  protected get plaintiffHint(): string {
    const lead = this.field.required ? 'Required.' : 'Optional.';
    const hint = this.field.hint?.trim();
    return hint ? `${lead} ${/[.!?]$/.test(hint) ? hint : `${hint}.`}` : lead;
  }

  protected readonly unitTypeKey = 'property_unit_type';
  protected readonly stateKey = 'property_state';
  protected readonly states = Object.keys(STATES);

  /** Used when a caller supplies no control — ee-field needs one to bind. */
  protected readonly inert = new FormControl({ value: null, disabled: true });

  private readonly partControls = new Map<string, FormControl>();
  private lastDefinition: FieldDefinitionView | null | undefined = undefined;
  private lastItem: LayoutItem | undefined = undefined;
  private lastField: CustomField | undefined = undefined;

  private readonly sanitizer = inject(DomSanitizer);
  private lastRichText: string | undefined = undefined;
  private lastRichHtml: SafeHtml = '';

  /**
   * A RICH_TEXT block's markup through the message allow-list (the same one the message view uses).
   *
   * Angular's sanitizer strips every `style`, which is where Quill keeps colour and highlight; the
   * allow-list keeps only safe colours and removes scripts, handlers and unsafe URLs itself. Memoised on
   * the text so change detection does not re-parse it every pass.
   */
  protected get richHtml(): SafeHtml {
    const text = this.item.text ?? '';
    if (this.lastRichText !== text) {
      this.lastRichText = text;
      this.lastRichHtml = this.sanitizer.bypassSecurityTrustHtml(allowMessageEmphasisStyles(text));
    }
    return this.lastRichHtml;
  }

  /** Memoised on (definition, item) identity so change detection does not rebuild it every pass. */
  protected get field(): CustomField {
    if (this.lastField === undefined || this.lastDefinition !== this.definition || this.lastItem !== this.item) {
      this.lastDefinition = this.definition;
      this.lastItem = this.item;
      this.lastField = customFieldFor(this.definition, this.item);
    }
    return this.lastField;
  }

  protected get parts(): CompositePart[] {
    return this.item.composite_type ? (COMPOSITE_PARTS[this.item.composite_type] ?? []) : [];
  }

  protected get compositeLabel(): string {
    const base =
      this.item.label_override || (this.item.composite_type ? COMPOSITE_LABELS[this.item.composite_type] : 'Details');
    return this.item.required_intake ? `${base} *` : base;
  }

  protected asArray(control: AbstractControl | null): FormArray | null {
    return control instanceof FormArray ? control : null;
  }

  protected partControl(key: string): FormControl {
    if (!this.builder) {
      const control = this.form?.get(key);
      if (control instanceof FormControl) return control;
    }
    let inert = this.partControls.get(key);
    if (!inert) {
      inert = new FormControl({ value: null, disabled: true });
      this.partControls.set(key, inert);
    }
    return inert;
  }

  protected partValue(key: string): string {
    return this.form?.get(key)?.value ?? '';
  }
}
